import { useState } from "react";

import type { ClassLifecycleSummary, LifecycleEvent } from "../../services/lifecycleService";
import { ClientRegister, type RegisterColumn } from "../register/ClientRegister";
import { DetailsModal } from "../register/DetailsModal";
import { LearnerHistory } from "./LifecycleParts";
import { EVENT_LABELS, classSummaryConfig, day, eventConfig } from "./lifecycleLogic";

const count = (value: number, tone = "text-amber-700") => (value ? <span className={`font-semibold ${tone}`}>{value}</span> : <span className="text-gray-400">0</span>);

/** Each class of the year and the lifecycle work waiting in it. */
export function ClassSummaryPanel({ rows, yearName, isLoading, isError, onRetry, onOpen }: {
  rows?: ClassLifecycleSummary[];
  yearName: string;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  onOpen: (tab: "promotions" | "graduations" | "transfers", className: string) => void;
}) {
  const columns: RegisterColumn<ClassLifecycleSummary>[] = [
    { header: "Class", cell: (row) => <span className="font-medium text-gray-900">{row.class_name}</span>, exportValue: (row) => row.class_name },
    { header: "Learners", cell: (row) => row.total_students, exportValue: (row) => row.total_students, align: "right" },
    { header: "Promotions waiting", cell: (row) => count(row.pending_promotions), exportValue: (row) => row.pending_promotions, align: "right" },
    { header: "Meet the criteria", cell: (row) => count(row.eligible_for_promotion, "text-emerald-700"), exportValue: (row) => row.eligible_for_promotion, align: "right" },
    { header: "Graduations waiting", cell: (row) => count(row.pending_graduations), exportValue: (row) => row.pending_graduations, align: "right" },
    { header: "Transfer requests", cell: (row) => count(row.transfer_requests), exportValue: (row) => row.transfer_requests, align: "right" },
  ];
  const next = (row: ClassLifecycleSummary) => (row.pending_graduations ? "graduations" : row.transfer_requests && !row.pending_promotions ? "transfers" : "promotions");
  return (
    <ClientRegister
      title={`Classes in ${yearName}`}
      description="Each class and the lifecycle work waiting in it: promotions and graduations to decide, and transfer requests. Open a class to see its proposals."
      rows={rows}
      config={classSummaryConfig}
      columns={columns}
      rowKey={(row) => row.class_id}
      onOpen={(row) => onOpen(next(row), row.class_name)}
      openLabel={(row) => ({ promotions: "Promotions", graduations: "Graduations", transfers: "Transfers" })[next(row)]}
      noun={["class", "classes"]}
      exportName={{ file: "lifecycle-by-class", title: `Lifecycle by Class — ${yearName}` }}
      isLoading={isLoading}
      isError={isError}
      onRetry={onRetry}
      emptyText={`${yearName} has no classes.`}
    />
  );
}

/** Every recorded promotion, move, graduation and change of standing: the audit trail. */
export function HistoryPanel({ rows, isLoading, isError, onRetry }: { rows?: LifecycleEvent[]; isLoading: boolean; isError: boolean; onRetry: () => void }) {
  const [viewing, setViewing] = useState<number | null>(null);
  const columns: RegisterColumn<LifecycleEvent>[] = [
    { header: "Date", cell: (row) => day(row.event_date), exportValue: (row) => day(row.event_date) },
    { header: "Learner", cell: (row) => <span><span className="font-medium text-gray-900">{row.student_name}</span> <span className="text-xs text-gray-500">{row.student_id}</span></span>, exportValue: (row) => row.student_name },
    { header: "Student ID", cell: (row) => row.student_id, exportValue: (row) => row.student_id, exportOnly: true },
    { header: "Event", cell: (row) => EVENT_LABELS[row.event_type] ?? row.event_type, exportValue: (row) => EVENT_LABELS[row.event_type] ?? row.event_type },
    { header: "Change", cell: (row) => (row.previous_status || row.new_status ? `${row.previous_status || "—"} → ${row.new_status || "—"}` : "—"), exportValue: (row) => `${row.previous_status} → ${row.new_status}` },
    { header: "What was recorded", cell: (row) => <span className="line-clamp-1 max-w-sm">{row.description}</span>, exportValue: (row) => row.description },
    { header: "Recorded by", cell: (row) => row.created_by_name || "—", exportValue: (row) => row.created_by_name ?? "" },
  ];
  const row = viewing ? rows?.find((item) => item.id === viewing) : undefined;
  return (
    <>
      <ClientRegister
        title="Lifecycle history"
        description="The dated record of every promotion, transfer, graduation, suspension, restoration, withdrawal and expulsion, with who recorded it."
        rows={rows}
        config={eventConfig}
        columns={columns}
        rowKey={(item) => item.id}
        onOpen={(item) => setViewing(item.id)}
        noun={["event", "events"]}
        exportName={{ file: "lifecycle-history", title: "Learner Lifecycle History" }}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText="Nothing has been recorded yet."
      />
      {row && (
        <DetailsModal
          title={row.student_name}
          headline={{ label: EVENT_LABELS[row.event_type] ?? row.event_type, value: day(row.event_date), note: row.academic_year_name }}
          facts={[["Student ID", row.student_id], ["Before", row.previous_status || "—"], ["After", row.new_status || "—"], ["Recorded by", row.created_by_name || "—"]]}
          sections={[{ title: "What was recorded", body: row.description }, { title: "Notes", body: row.notes !== row.description ? row.notes : "" }]}
          onClose={() => setViewing(null)}
        >
          <LearnerHistory studentId={row.student} />
        </DetailsModal>
      )}
    </>
  );
}
